import React from 'react';
import { Layers, FlaskConical, Cog, Heart } from 'lucide-react';

const PhilosophySection = ({ content }) => {
  // Brand Pillars from GHF Executive Brand Manual
  const defaultValues = [
    { title: 'Structure Over Hacks', description: 'Quick fixes don\'t last. Our guides give you complete systems that create real, lasting change in your family.' },
    { title: 'Science Over Trends', description: 'Built on child development science and emotional intelligence research—not the latest parenting fad.' },
    { title: 'Systems Over Motivation', description: 'When you have the right system, you don\'t need willpower. Clear steps make consistency automatic.' },
    { title: 'Connection Over Control', description: 'True transformation comes through understanding, not punishment. Build bonds that last.' },
  ];
  const cmsCards = Array.isArray(content?.cards) && content.cards.length ? content.cards : defaultValues;
  const cardIcons = [Layers, FlaskConical, Cog, Heart];
  const values = cmsCards.map((c, i) => ({
    icon: cardIcons[i % cardIcons.length],
    title: c.title || '',
    description: c.description || '',
  }));
  const eyebrow = content?.eyebrow || 'Our Approach';
  const heading = content?.heading || 'Parenting Shouldn\u2019t Be This Hard';
  const supportingCopy = content?.supporting_copy || 'We\u2019re here to simplify the journey\u2014with research-backed systems that respect both you and your child.';

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-[#F8FAFC]" data-testid="philosophy-section">
      <div className="wrap">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-[#0F766E] font-semibold text-sm uppercase tracking-wider">{eyebrow}</span>
          <h2 className="text-3xl lg:text-[40px] font-bold mt-3 mb-4 text-[#0B1F3A]">
            {heading}
          </h2>
          <p className="text-[#475569] max-w-2xl mx-auto text-lg">
            {supportingCopy}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {values.map((value) => (
            <div 
              key={value.title}
              className="bg-white rounded-2xl p-6 lg:p-8 border border-[#E2E8F0] hover:border-[#0F766E]/20 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0F766E]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0F766E] transition-colors">
                  <value.icon className="h-6 w-6 text-[#0F766E] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-[#0B1F3A]">
                    {value.title}
                  </h3>
                  <p className="text-[#475569] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial with specific result */}
        <div className="mt-14 text-center">
          <blockquote className="max-w-2xl mx-auto bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-sm">
            <p className="text-xl text-[#0B1F3A] italic leading-relaxed mb-4">
              &ldquo;These systems changed everything. Bedtime used to be a 2-hour battle—now it&apos;s 20 minutes of calm connection. My 6-year-old actually looks forward to our routine.&rdquo;
            </p>
            <footer className="text-[#475569]">
              <strong className="text-[#0B1F3A]">— Sarah M.</strong>, Mom of 3 (ages 4, 6, 9)
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;
